<template>
  <div ref="Sidebar" :class="['sidebar transparent', { scrolled: isScrolled }]">
    <template v-if="windowWidth < breakpoints.small">
      <div class="sidebar-textstate-container">
        <div>
          <button @click="animateMenu()" class="sidebar-textstate-container-cta" type="button">
            <font-awesome-icon :icon="['fas', menuState ? 'ellipsis-v' : 'ellipsis-h']" />
          </button>
        </div>
      </div>
      <div
        v-show="menuState"
        :class="['sidebar-mobile-container', { scrolled: isScrolled }]"
        ref="SidebarMobileRoutes"
      >
        <div :class="['sidebar-mobile-container-wrapper', { scrolled: isScrolled }]">
          <div
            @click="animateMenu(false)"
            :class="['sidebar-mobile-container-wrapper-backdrop', { scrolled: isScrolled }]"
          ></div>
          <sidebar-routes :routes="$props.routes" />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="sidebar-textstate-container">
        <button
          @click="textState = !textState"
          class="sidebar-textstate-container-cta"
          type="button"
        >
          <font-awesome-icon :icon="['fas', textState ? 'caret-left' : 'caret-right']" />
        </button>
      </div>
      <sidebar-routes :routes="$props.routes" :textState="textState" />
    </template>
  </div>
</template>

<script>
const SidebarRoutes = () =>
  import(
    /* webpackChunkName: "SidebarComponent" */ "@/components/Global/Sidebar/Routes/SidebarRoutes.vue"
  );

export default {
  name: "Sidebar",
  components: {
    SidebarRoutes
  },
  props: {
    /*
     * @property - routes
     * Sets routes of component
     */
    routes: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      textState: true,
      menuState: false
    };
  },
  computed: {
    /*
     * @computed - windowWidth
     * Gets window width from vuex state
     */
    windowWidth() {
      return this.$store.state.Global.window.width;
    },
    /*
     * @computed - breakpoints
     * Gets breakpoints from utils plugin
     */
    breakpoints() {
      return this.$utils.styles.breakpoints;
    },
    /*
     * @computed - isScrolled
     * True is window scrollY is bigger or equal to 10
     */
    isScrolled() {
      return this.$store.state.Global.window.scroll >= 10;
    }
  },
  methods: {
    /*
     * @function - animateMenu
     * Calls fade in ot out for menu, and sets if mobile sidebar routes cta should be enabled or disabled
     * @param - none
     */
    animateMenu(state = null) {
      const newState = state !== null ? state : !this.menuState;

      this.m_animateFade(this.$refs.SidebarMobileRoutes, 500, this.menuState ? "out" : "in").then(
        () => {
          if (!newState) {
            this.menuState = newState;
          }
        }
      );

      if (newState) {
        this.menuState = newState;
      }
    }
  },
  created() {
    this.$bus.$on("SidebarRoutes: anchor clicked", this.animateMenu);
  },
  mounted() {
    this.textState = this.windowWidth >= this.breakpoints.large;
  },
  beforeDestroy() {
    this.$bus.$off("SidebarRoutes: anchor clicked", this.animateMenu);
  }
};
</script>

<style lang="scss" scoped>
.sidebar {
  align-self: flex-start;
  background-color: $gray-blue;
  position: fixed;
  top: 4.85rem;
  z-index: 720;

  &.scrolled {
    top: 2.5rem;
  }

  @include breakpoint($small) {
    max-height: calc(100vh - 7.5rem);
    overflow-y: auto;
    padding: 1.25rem 1.25rem 1.25rem 0;
    position: sticky;
    top: 3rem;

    &.scrolled {
      max-height: calc(100vh - 5.5rem);
      top: 3rem;
    }
  }

  @include breakpoint($large) {
    max-height: calc(100vh - 8.25rem);
    top: 3.75rem;

    &.scrolled {
      max-height: calc(100vh - 6.25rem);
      top: 3.75rem;
    }
  }

  &-textstate-container {
    position: absolute;
    z-index: 2;

    @include breakpoint($small) {
      height: 2rem;
      border-bottom: 0.0625rem solid $main-blue;
      margin-bottom: 0.35rem;
      position: relative;
    }

    &-cta {
      @include fillHorizontalTransition($dark-gray, $white-gray, $white-raw, $main-blue, true);
      border: none;
      box-shadow: 0.2rem 0.2rem 0.25rem 0 rgba($dark-raw, 0.4);
      font-size: 1rem;
      height: 1.75rem;
      margin: 0 0.25rem;
      padding: 0;
      width: 1.75rem;

      @include breakpoint($small) {
        @include fillHorizontalTransition($white-raw, $dark-gray, $white-raw, $main-blue, true);
        border-radius: 0.2rem;
        float: right;
        height: 1.3rem;
        margin: 0.15rem 0 0 0.5rem;
        width: 1.3rem;

        &::before {
          border-radius: 0.2rem;
        }
      }
    }
  }

  &-mobile-container {
    left: 0;
    max-height: calc(100vh - 4.25rem);
    position: fixed;
    overflow: auto;
    top: 4.25rem;
    transition: background-color 1.5s ease, top 0.5s ease;
    z-index: 1;

    &.scrolled {
      max-height: calc(100vh - 2.25rem);
      top: 2.25rem;
    }

    &-wrapper {
      background-color: $white-gray;
      border-right: 0.0625rem solid $main-green;
      padding: 3rem 1.25rem 3rem 0;
      transition: background-color 1.5s ease, top 0.5s ease;
      z-index: 0;

      &-backdrop {
        background-color: rgba($dark-raw, 0.25);
        left: 0;
        height: calc(100vh - 4.25rem);
        right: 0;
        position: fixed;
        top: 4.25rem;
        transition: background-color 1.5s ease, top 0.5s ease;
        z-index: -1;

        &.scrolled {
          height: calc(100vh - 2.25rem);
          top: 2.25rem;
        }
      }

      &.scrolled {
        background-color: rgba($white-gray, 0.85);
      }
    }
  }
}
</style>
